import { useState, useCallback, useRef } from "react";
import { Combobox, ComboboxOption } from "@altimateai/lego";

interface FilterDropdownInputProps {
  label?: string;
  value: string[] | string;
  onChange: (value: string[] | string) => void;
  options: ComboboxOption[];
  multiSelect?: boolean;
  // API props for async loading
  apiFunction?: (params: { prefix?: string; page?: number; size?: number }) => Promise<{
    items: { label: string; value: string }[];
    pages: number;
    total: number;
  }>;
  seeMore?: boolean;
  pageSize?: number;
}

const FilterDropdownInput = ({
  label,
  value,
  onChange,
  options,
  multiSelect = true,
  apiFunction,
  seeMore = false,
  pageSize,
}: FilterDropdownInputProps) => {
  const [apiOptions, setApiOptions] = useState<ComboboxOption[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  // Use ref to stabilize apiFunction reference so downstream callbacks don't change identity
  const apiFunctionRef = useRef(apiFunction);
  apiFunctionRef.current = apiFunction;

  const handleComboboxChange = (newValue: string[] | string) => {
    onChange(newValue);
  };

  const handleLoadMore = useCallback(
    async (searchValue?: string, page?: number) => {
      if (!apiFunctionRef.current) return;

      try {
        const response = await apiFunctionRef.current!({
          prefix: searchValue || "",
          page: page || 1,
          ...(pageSize ? { size: pageSize } : {}),
        });

        const comboboxOptions: ComboboxOption[] = response.items.map(item => ({
          label: item.label,
          value: item.value,
        }));

        if (page === 1) {
          // Reset options for new search
          setApiOptions(comboboxOptions);
        } else {
          // Append options for pagination
          setApiOptions(prev => [...prev, ...comboboxOptions]);
        }

        const newHasMore = page ? page < response.pages : response.pages > 1;
        setCurrentPage(page || 1);
        setHasMore(newHasMore);
      } catch (error) {
        console.error("Failed to load options:", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiFunction accessed via apiFunctionRef to keep callback identity stable and prevent re-triggering initial load
    [pageSize]
  );

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (apiFunctionRef.current) {
        // For API calls, trigger new search starting from page 1
        handleLoadMore(searchValue, 1);
      }
    },
    [handleLoadMore]
  );

  const handlePageReset = useCallback(() => {
    setCurrentPage(1);
    setApiOptions([]);
  }, []);

  // Use API options if apiFunction is provided, otherwise use static options
  const comboboxOptions = apiFunction ? apiOptions : options;

  return (
    <Combobox
      options={comboboxOptions}
      value={value}
      onChange={handleComboboxChange}
      placeholder={`Select ${label}...`}
      searchPlaceholder={`Search ${label}...`}
      multiSelect={multiSelect}
      showApplyButton={false}
      showClearButton={multiSelect}
      buttonProps={{
        "aria-label": `Select ${label}...`,
        className: "al-w-full al-min-w-0 al-max-w-full al-justify-start",
      }}
      // API-specific props
      // onLoadMore is always wired when apiFunction is present so Combobox
      // fires the initial empty-string load on mount and populates options
      // before the user types anything.  hasMore / pagination are only
      // forwarded when the caller opts into "See more" paging.
      {...(apiFunction && {
        onLoadMore: handleLoadMore,
        hasMore: seeMore ? hasMore : false,
        onSearch: handleSearch,
        onPageReset: handlePageReset,
        currentPage: currentPage,
      })}
    />
  );
};

export default FilterDropdownInput;
